// pages/index/components/EventView.tsx
import {
  ArrowBack,
  DirectionsOutlined,
  Close,
  Link as LinkIcon,
  CheckCircle,
  ConfirmationNumber as ConfirmationNumberIcon,
  MoreHoriz,
  ChevronRight,
  Image as ImageIcon,
  EditOutlined,
  CalendarMonth,
} from "@mui/icons-material";
import { ImagePreview } from "@root/components/common/ImagePreview";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItemButton,
  Stack,
  Typography,
  styled,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  Avatar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AppSnackbar } from "@root/components/common/AppSnackbar";
import { useSnackbar } from "@root/hooks/useSnackbar";
import { SwitchToUserAccountModal } from "@root/components/modals/SwitchToUserAccountModal";
import { ConfirmModal } from "@root/components/modals/ConfirmModal";
import { ReportEventModal } from "@root/components/modals/ReportEventModal";
import { ShareModal } from "@root/components/modals/ShareModal";
import { RecurringEventEditModal } from "@root/components/modals/RecurringEventEditModal";
import { navigate } from "vike/client/router";
import { DeleteScope } from "@root/utils/types";
import { eventService } from "@root/services/api/eventService";
import { organizationService } from "@root/services/api/organizationService";
import { userService } from "@root/services/api/userService";
import { subscriptionService } from "@root/services/api/subscriptionService";
import { hasStripePayoutFeatures } from "@root/utils/featureIds";
import { TICKETING_ENABLED } from "@root/components/constants/constants";
import { useAccountContext } from "@root/stores/accountStore";
import { ModalStore } from "@root/stores/modalStore";
import { Event, Organization, Pricing, Address } from "@root/utils/entities";
import {
  downloadFile,
  formatEventDateRange,
  getApiError,
  getEventShareUrl,
  getGoogleDirectionsUrl,
  hasErrors,
} from "@root/utils/functions";
import { observer, useLocalObservable } from "mobx-react-lite";
import moment from "moment";
import { PropsWithChildren, useState, useEffect, useRef } from "react";
import { formatRecurrenceSchedule } from "@root/utils/rruleHelpers";
import { DigiFreeBadge } from "./DigiFreeBadge";
import { DigiFreeModal } from "./DigiFreeModal";
import followButton from "@images/follow.svg";
import followingButton from "@images/following.svg";
import hideButton from "@images/hide.svg";
import unhideButton from "@images/unhide.svg";
import dollarIcon from "@images/dollar.svg";
import personIcon from "@images/person.svg";
import locationIcon from "@images/location.svg";
import { EventLocationMap } from "./map/EventLocationMap";
import reportIcon from "@images/report.svg";
import attendIcon from "@images/attend.svg";
import dateAndTimeIcon from "@images/dateAndTime.svg";
import shareIcon from "@images/share.svg";
import { APP_MODAL_MAX_WIDTH } from "@root/components/modals/modalConstants";
import { CalendarPickerDialog } from "@root/components/calendar/CalendarPickerDialog";
import { CalendarEventData } from "@root/components/calendar/CalendarEventData";

/**
 * Drop-in refactor: compact, banner-first layout with gradient overlay and floating close.
 * All existing logic/handlers (attendEvent, followEvent, etc.) are untouched.
 */

// removed formatAddressDisplay

// Returns address as array of lines for multi-line display (matching mobile UI)
const formatAddressLines = (address?: Address): string[] => {
  if (!address) return ["Location TBD"];

  const { addressLine1, addressLine2, city, state, zip, country } = address;
  const lines: string[] = [];

  if (addressLine1) lines.push(addressLine1);
  if (addressLine2) lines.push(addressLine2);

  const locationParts: string[] = [];
  if (city) locationParts.push(city);
  if (state) locationParts.push(state);

  let locationString = locationParts.join(", ");
  if (zip) {
    locationString += (locationString ? ", " : "") + zip.toUpperCase();
  }

  if (locationString) lines.push(locationString);

  if (country) {
    const countryDisplay =
      country === "US"
        ? "United States"
        : country === "CA"
          ? "Canada"
          : country;
    lines.push(countryDisplay);
  }

  return lines.length > 0 ? lines : ["Location TBD"];
};

type EventViewProps = {
  event: Event;
  onOrganizationClick: (
    organization: Organization,
    fromVendorsList?: boolean,
    fromVenue?: boolean,
  ) => void;
  onClose?: () => void;
  onFollowChange?: (isFollowed: boolean) => void;
  onHideChange?: (eventId: string, isHidden: boolean) => void;
  reopenVendorsListToken?: number;
};

type EventViewState = {
  isLoading: boolean;
  confirmTitle?: string;
  confirmMessage?: string;
  onConfirm: () => Promise<void>;
};

export const EventView = ({
  event,
  onOrganizationClick,
  onClose,
  onFollowChange,
  onHideChange,
  reopenVendorsListToken,
}: EventViewProps) => {
  const accountStore = useAccountContext();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const confirmModalStore = useLocalObservable(() => new ModalStore());
  const shareModalStore = useLocalObservable(() => new ModalStore());
  const reportModalStore = useLocalObservable(() => new ModalStore());
  const editRecurringModalStore = useLocalObservable(() => new ModalStore());

  const [state, setState] = useState<EventViewState>({
    isLoading: false,
    onConfirm: async () => {},
  });

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  // Use optimistic state only during API calls, read directly from event otherwise
  const [optimisticFollowState, setOptimisticFollowState] = useState<
    boolean | null
  >(null);
  const [optimisticHideState, setOptimisticHideState] = useState<
    boolean | null
  >(null);

  const [isDigiFreeModalOpen, setIsDigiFreeModalOpen] = useState(false);
  const [isVendorsModalOpen, setIsVendorsModalOpen] = useState(false);
  const [selectedPricingId, setSelectedPricingId] = useState<string>();
  const [isBookingCheckoutLoading, setIsBookingCheckoutLoading] =
    useState(false);
  const [isOrgBookingDialogOpen, setIsOrgBookingDialogOpen] = useState(false);
  const [isCalendarPickerOpen, setIsCalendarPickerOpen] = useState(false);
  const [calendarOpenPending, setCalendarOpenPending] = useState(false);
  const attendAreaRef = useRef<HTMLDivElement>(null);

  // Menu anchor for "More" options
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const isMoreMenuOpen = Boolean(moreAnchorEl);
  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  const openVendorOrganization = async (organizationId?: string) => {
    if (!organizationId) {
      setState((previous) => ({
        ...previous,
        error: "Organization details are not available for this vendor.",
      }));
      return;
    }

    try {
      const response =
        await organizationService.getOrganization(organizationId);
      const organization = response.data?.organization;
      if (!organization) {
        setState((previous) => ({
          ...previous,
          error: "Organization details are not available for this vendor.",
        }));
        return;
      }

      setIsVendorsModalOpen(false);
      onOrganizationClick(organization, true);
    } catch (error) {
      setState((previous) => ({
        ...previous,
        error:
          error instanceof Error
            ? error.message
            : "Unable to load organization details.",
      }));
    }
  };

  // Track if organization has active subscription
  const [hasActiveSubscription, setHasActiveSubscription] =
    useState<boolean>(false);
  const [isCheckingSubscription, setIsCheckingSubscription] =
    useState<boolean>(true);
  const [orgHasPayoutFeatures, setOrgHasPayoutFeatures] =
    useState<boolean>(false);

  // Read directly from event object as source of truth
  // Use optimistic state only during API calls
  const currentFollowState =
    optimisticFollowState !== null
      ? optimisticFollowState
      : event.isFollowed || false;

  const currentHideState =
    optimisticHideState !== null
      ? optimisticHideState
      : event.isHidden || false;

  // Check if event is created by current user
  const isCreatedByCurrentUser = accountStore.isUserProfile
    ? !!accountStore.account?.id &&
      !event.organizationId &&
      event.user?.id === accountStore.account.id
    : !!accountStore.session?.id &&
      event.organizationId === accountStore.session.id;

  // Event is part of a recurrence series if flag is true OR has parentEventId.
  const isRecurringSeries = event.isRecurring || !!event.parentEventId;

  const handleEditEvent = async () => {
    handleMoreClose();
    if (isRecurringSeries) {
      editRecurringModalStore.open();
      return;
    }
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    await navigate(`/account/events/${event.id}?returnTo=${returnTo}`);
  };

  const handleEditWithScope = async (scope: DeleteScope) => {
    editRecurringModalStore.close();
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    await navigate(`/account/events/${event.id}?scope=${scope}&returnTo=${returnTo}`);
  };

  // Pre-compute human‑readable recurrence schedule text (if any)
  const recurrenceScheduleText = formatRecurrenceSchedule({
    recurrence: event.recurrence ?? null,
    recurrenceRule: event.recurrenceRule,
    dateRange: {
      start: event.dateRange?.start
        ? moment(event.dateRange.start as any)
        : undefined,
    },
  });

  // Fetch subscription status for organization
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      setIsCheckingSubscription(true);
      if (event.organization?.id) {
        try {
          const response =
            await subscriptionService.getCurrentByOrganizationIdWithDetails(
              event.organization.id,
            );
          const subscription = response?.data as any;
          const isActive =
            subscription?.isActive === true || subscription?.isActive === 1;
          // A plan is paid if its actual list price (monthly or annual) is > 0.
          // This correctly handles coupon-zero plans (buyingPrice=0 but plan price > 0).
          const pricing =
            subscription?.pricing ?? subscription?.planVersion ?? {};
          const isPaidSubscription =
            (pricing.monthlyPrice ?? 0) > 0 || (pricing.annualPrice ?? 0) > 0;

          const hasPayoutFeatures = hasStripePayoutFeatures(
            subscription?.features ?? [],
          );

          setHasActiveSubscription(!!isActive && isPaidSubscription);
          setOrgHasPayoutFeatures(hasPayoutFeatures);
        } catch (error) {
          console.error("Error fetching subscription status:", error);
          setHasActiveSubscription(false);
          setOrgHasPayoutFeatures(false);
        } finally {
          setIsCheckingSubscription(false);
        }
      } else {
        setHasActiveSubscription(false);
        setOrgHasPayoutFeatures(false);
        setIsCheckingSubscription(false);
      }
    };

    checkSubscriptionStatus();
  }, [event.organization?.id]);

  useEffect(() => {
    if (reopenVendorsListToken && reopenVendorsListToken > 0) {
      setIsVendorsModalOpen(true);
    }
  }, [reopenVendorsListToken]);

  const bookablePricingOptions = (event.pricing || []).filter(
    (pricing) => !!pricing.id && (pricing.price ?? 0) > 0,
  );

  const isEventEnded = event.dateRange?.end
    ? moment
        .utc(event.dateRange.end as any)
        .local()
        .isBefore(moment())
    : false;

  useEffect(() => {
    setSelectedPricingId(bookablePricingOptions[0]?.id);
  }, [event.id, event.pricing]);

  useEffect(() => {
    if (calendarOpenPending && (event.isAttended ?? false) && attendAreaRef.current) {
      setIsCalendarPickerOpen(true);
      setCalendarOpenPending(false);
    }
  }, [calendarOpenPending, event.isAttended]);


  // --- Existing logic preserved verbatim (only formatting lightly) ---
  const getAgeRange = (min?: number, max?: number) => {
    if (min === undefined && max === undefined) return `All ages`;
    if (min === max) return `Age ${min}`;
    if (min === 0 && max === 65) return `All ages`;
    if (!max) return `Ages ${min} and over`;
    if (!min) return `Ages ${max} and under`;
    return `Ages ${min} to ${max}`;
  };

  // Helper to check if image URL is valid (not empty, not __REMOVE__ marker)
  // Backend bug: returns S3 URL with __REMOVE__ as filename instead of null
  const isValidImageUrl = (url?: string | null): boolean => {
    if (!url || url.trim() === "") return false;
    if (url.includes("__REMOVE__")) return false;
    return true;
  };

  const attendEvent = async () => {
    showLoadingSpinner();

    const response = await userService.attendEvent(
      accountStore.account!.id,
      event.id,
    );

    if (hasErrors(response)) {
      showError(getApiError(response).message);
    } else {
      event.isAttended = response.data!.isAttended;
      event.isFollowed = response.data!.isFollowed;
      hideLoadingSpinner();
      setCalendarOpenPending(true);
    }
  };

  const followEvent = async () => {
    showLoadingSpinner();

    // Get current state from event object
    const currentState = event.isFollowed || false;

    // Optimistic update - immediately show the new state
    const newOptimisticState = !currentState;
    setOptimisticFollowState(newOptimisticState);

    // Call the appropriate endpoint based on current follow state
    const response = currentState
      ? await userService.unfollowEvent(accountStore.account!.id, event.id)
      : await userService.followEvent(accountStore.account!.id, event.id);

    if (hasErrors(response)) {
      showError(getApiError(response).message);
      // Revert optimistic update on error
      setOptimisticFollowState(null);
    } else {
      // Update event object and clear optimistic state
      event.isFollowed = newOptimisticState;
      setOptimisticFollowState(null);
      onFollowChange?.(newOptimisticState);

      // Verify with backend
      if (accountStore.account?.id) {
        userService
          .getFollowedEvents(accountStore.account.id)
          .then((followedResponse) => {
            if (!hasErrors(followedResponse)) {
              const followedEventIds = new Set(
                followedResponse.data?.events?.map((evt) => evt.id) ?? [],
              );
              const verifiedFollowState = followedEventIds.has(event.id);
              event.isFollowed = verifiedFollowState;
              onFollowChange?.(verifiedFollowState);
            }
          });
      }

      showSuccessMessage(
        newOptimisticState
          ? "You're now following this event."
          : "You've unfollowed this event.",
      );
    }
  };

  const hideEvent = async () => {
    showLoadingSpinner();

    // Get current state from event object
    const currentState = event.isHidden || false;

    // Optimistic update - immediately show the new state
    const newOptimisticState = !currentState;
    setOptimisticHideState(newOptimisticState);

    // Call the appropriate endpoint based on current hide state
    const response = currentState
      ? await userService.unhideEvent(accountStore.account!.id, event.id)
      : await userService.hideEvent(accountStore.account!.id, event.id);

    if (hasErrors(response)) {
      showError(getApiError(response).message);
      // Revert optimistic update on error
      setOptimisticHideState(null);
    } else {
      // Update event object and clear optimistic state
      event.isHidden = newOptimisticState;
      setOptimisticHideState(null);

      showSuccessMessage(
        newOptimisticState
          ? "Event hidden successfully."
          : "Event unhidden successfully.",
      );

      // Notify parent component about the hide change
      onHideChange?.(event.id, newOptimisticState);

      // Close the modal and go back after hide/unhide action
      if (onClose) {
        // Small delay to show success message
        setTimeout(() => {
          onClose();
        }, 500);
      }
    }
  };

  const reportEvent = async (reason: string) => {
    showLoadingSpinner();

    const response = await userService.reportEvent(
      accountStore.account!.id,
      event.id,
      reason,
    );

    if (hasErrors(response)) {
      showError(getApiError(response).message);
    } else {
      showSuccessMessage("You've reported this event.");
    }
  };

  const downloadCalendarFile = async () => {
    showLoadingSpinner();

    const response = await eventService.getCalendarFile(event.id);

    if (hasErrors(response)) {
      showError(getApiError(response).message);
    } else {
      downloadFile("event.ics", response.data!);
      hideLoadingSpinner();
    }
  };

  const showLoadingSpinner = () =>
    setState({
      ...state,
      isLoading: true,
    });

  const hideLoadingSpinner = () =>
    setState({
      ...state,
      isLoading: false,
    });

  const showSuccessMessage = (message: string) => {
    setState({ ...state, isLoading: false });
    showSnackbar(message, "success");
  };

  const showError = (message?: string) => {
    setState({ ...state, isLoading: false });
    showSnackbar(message ?? "Something went wrong.", "error");
  };

  const calendarEventData: CalendarEventData = {
    title: event.name ?? "",
    startUtc: event.dateRange?.start
      ? moment(event.dateRange.start).utc().toDate()
      : new Date(),
    endUtc: event.dateRange?.end
      ? moment(event.dateRange.end).utc().toDate()
      : new Date(),
    description: event.description ?? undefined,
    location: event.location?.address
      ? [
          event.location.address.addressLine1,
          event.location.address.city,
          event.location.address.state,
        ]
          .filter(Boolean)
          .join(", ")
      : undefined,
  };

  const startEventCheckout = async () => {
    if (!selectedPricingId) {
      showError("Please choose a ticket option before continuing.");
      return;
    }

    setIsBookingCheckoutLoading(true);

    const response = await eventService.createEventCheckoutSession(
      event.id,
      selectedPricingId,
    );

    if (hasErrors(response)) {
      setIsBookingCheckoutLoading(false);
      showError(getApiError(response).message);
      return;
    }

    const checkoutUrl = response.data?.url;
    if (!checkoutUrl) {
      setIsBookingCheckoutLoading(false);
      showError("There was an issue starting checkout. Please try again.");
      return;
    }

    window.location.assign(checkoutUrl);
  };

  // --- UI rendering: banner + compact details + actions ---
  return (
    <CardContainer>
      {/* Fixed Header Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          height: 56,
          borderBottom: "1px solid #E0E0E0",
          flexShrink: 0,
          backgroundColor: "#fff",
          zIndex: 10,
        }}
      >
        <Typography
          sx={{
            fontSize: "1.125rem",
            fontWeight: 600,
            color: "#000",
          }}
        >
          Event Details
        </Typography>
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              width: 36,
              height: 36,
              color: "#000",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            <Close sx={{ fontSize: "1.25rem" }} />
          </IconButton>
        )}
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
        }}
      >
        {/* Banner section - scrolls with content */}
        {isValidImageUrl(event.bannerImageUrl) && (
          <>
            <BannerWrapper>
              <ImagePreview
                src={event.bannerImageUrl}
                alt={event.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  objectPosition: "center",
                  display: "block",
                }}
              />
            </BannerWrapper>
            <Divider sx={{ borderColor: "#E0E0E0" }} />
          </>
        )}

        {/* Main content section */}
        <Box
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            mt: 0,
          }}
        >
          {/* Main details column — spacing matches mobile view_event_screen.dart paddingMedium (16px) */}
          <Stack spacing={2}>
            {/* Event Name */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: "1.5rem",
                lineHeight: 1.25,
                color: "#000",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {event.name}
            </Typography>

            {/* Date & Time */}
            {(() => {
              const dateRange = formatEventDateRange(
                moment(event.dateRange?.start),
                moment(event.dateRange?.end),
              );

              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    textAlign: "left",
                  }}
                >
                  <Box
                    component="img"
                    src={dateAndTimeIcon}
                    alt="Date and Time"
                    sx={{
                      width: "20px",
                      height: "20px",
                      mt: 0.25,
                    }}
                  />
                  <Stack spacing={0.5} sx={{ mt: 0.25 }}>
                    {dateRange.isOneDay ? (
                      <>
                        <Typography
                          sx={{
                            fontSize: "1rem",
                            color: "#000",
                            fontWeight: 600,
                            lineHeight: 1.4,
                          }}
                        >
                          {dateRange.date}
                        </Typography>
                        {dateRange.time && (
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: "#757575",
                              fontWeight: 400,
                            }}
                          >
                            {dateRange.time}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <Typography
                          sx={{
                            fontSize: "1rem",
                            color: "#000",
                            fontWeight: 600,
                            lineHeight: 1.4,
                          }}
                        >
                          {dateRange.startDate}
                        </Typography>
                        {dateRange.startTime && (
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: "#757575",
                              fontWeight: 400,
                            }}
                          >
                            {dateRange.startTime}
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            fontSize: "1rem",
                            color: "#000",
                            fontWeight: 600,
                            lineHeight: 1.4,
                            mt: 0.5,
                          }}
                        >
                          {dateRange.endDate}
                        </Typography>
                        {dateRange.endTime && (
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: "#757575",
                              fontWeight: 400,
                            }}
                          >
                            {dateRange.endTime}
                          </Typography>
                        )}
                      </>
                    )}
                    {isRecurringSeries && recurrenceScheduleText && (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                          ml: -0.75,
                          backgroundColor: "#F5F5F5",
                          borderRadius: "10px",
                          padding: "6px 12px",
                          width: "fit-content",
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 5V8L10.5 9.5"
                            stroke="#424242"
                            strokeWidth="1.45455"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M11.5 6.5H14V4"
                            stroke="#424242"
                            strokeWidth="1.45455"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M11.7749 12C10.9887 12.7419 10.0013 13.2356 8.93601 13.4196C7.87077 13.6035 6.77495 13.4694 5.78547 13.0341C4.79599 12.5988 3.95672 11.8816 3.37254 10.972C2.78835 10.0625 2.48515 9.00092 2.50082 7.92003C2.51649 6.83914 2.85034 5.78684 3.46064 4.8946C4.07095 4.00236 4.93065 3.30975 5.93234 2.90331C6.93402 2.49687 8.03327 2.39463 9.09273 2.60935C10.1522 2.82408 11.1249 3.34625 11.8893 4.11062C12.6249 4.85562 13.2324 5.55812 13.9999 6.5"
                            stroke="#424242"
                            strokeWidth="1.45455"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            color: "#424242",
                            fontWeight: 500,
                            lineHeight: 1.2,
                          }}
                        >
                          Event Repeats {recurrenceScheduleText}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              );
            })()}

            {/* Location — omitted when a Venue is linked; the Venue section below is the single source of truth for name+address in that case */}
            {!event.venueId && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Box
                  component="img"
                  src={locationIcon}
                  alt="Location"
                  sx={{ width: "1.25rem", height: "1.25rem", mt: 0.25 }}
                />
                <Box>
                  {event.locationName && (
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        color: "#000",
                        fontWeight: 600,
                        lineHeight: 1.4,
                      }}
                    >
                      {event.locationName}
                    </Typography>
                  )}
                  {formatAddressLines(event.location?.address).map(
                    (line, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          fontSize: "0.875rem",
                          color: "#757575",
                          fontWeight: 400,
                          lineHeight: 1.5,
                        }}
                      >
                        {line}
                      </Typography>
                    ),
                  )}
                </Box>
              </Box>
            )}

            {/* Event Link */}
            {event.eventLink && (
              <InfoRow sx={{ alignItems: "center", gap: 0.75 }}>
                <LinkIcon sx={{ fontSize: "1.25rem", color: "#1565C0" }} />
                <Link
                  href={event.eventLink}
                  target="_blank"
                  underline="always"
                  sx={{
                    overflowWrap: "anywhere",
                    textDecorationColor: "#1565C0",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#1565C0",
                      fontSize: "1rem",
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                  >
                    Visit Event Link
                  </Typography>
                </Link>
              </InfoRow>
            )}

            {/* Ticket Link */}
            {event.ticketLink && (
              <InfoRow sx={{ alignItems: "center", gap: 0.75 }}>
                <ConfirmationNumberIcon
                  sx={{ fontSize: "1.25rem", color: "#1565C0" }}
                />
                <Link
                  href={event.ticketLink}
                  target="_blank"
                  underline="always"
                  sx={{
                    overflowWrap: "anywhere",
                    textDecorationColor: "#1565C0",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#1565C0",
                      fontSize: "1rem",
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                  >
                    External Ticket Link
                  </Typography>
                </Link>
              </InfoRow>
            )}

            {/* Pricing */}
            {event.pricing && event.pricing.length > 0 && (
              <InfoRow sx={{ alignItems: "center", gap: 0.75 }}>
                <Box
                  component="img"
                  src={dollarIcon}
                  alt="Price"
                  sx={{ width: "1.25rem", height: "1.25rem" }}
                />
                <Pricings
                  pricings={event.pricing}
                  selectedPricingId={selectedPricingId}
                  onSelectPricing={setSelectedPricingId}
                  selectable={TICKETING_ENABLED && !!event.canBookNow && !isEventEnded}
                />
              </InfoRow>
            )}

            {/* Ages */}
            <InfoRow sx={{ alignItems: "center", gap: 0.75 }}>
              <Box
                component="img"
                src={personIcon}
                alt="Age Range"
                sx={{ width: "1.25rem", height: "1.25rem" }}
              />
              <Typography
                variant="body2"
                sx={{ fontSize: "0.9375rem", color: "#000", fontWeight: 400 }}
              >
                {getAgeRange(event.ageRange?.min, event.ageRange?.max)}
              </Typography>
            </InfoRow>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              {/* Book Now — ticketing feature, unchanged */}
              {TICKETING_ENABLED &&
                event.canBookNow &&
                !isEventEnded &&
                bookablePricingOptions.length > 0 &&
                orgHasPayoutFeatures && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (accountStore.isOrganizationProfile) {
                        setIsOrgBookingDialogOpen(true);
                      } else {
                        startEventCheckout();
                      }
                    }}
                    disabled={!selectedPricingId || isBookingCheckoutLoading}
                    sx={{
                      flex: 1,
                      height: 44,
                      borderRadius: "22px",
                      backgroundColor: "primary.main",
                      textTransform: "none",
                      fontSize: "0.9375rem",
                      fontWeight: 600,
                      gap: 0.75,
                      "&:hover": {
                        backgroundColor: "primary.dark",
                        opacity: 0.9,
                      },
                    }}
                  >
                    <ConfirmationNumberIcon sx={{ width: 20, height: 20, color: "#fff" }} />
                    {isBookingCheckoutLoading ? "Booking..." : "Book Now"}
                  </Button>
                )}

              {isCreatedByCurrentUser ? (
                /* OWNER (user or org): Edit Event + Share + Directions */
                <>
                  <Button
                    variant="contained"
                    onClick={handleEditEvent}
                    sx={{
                      height: 44,
                      borderRadius: "22px",
                      backgroundColor: "primary.main",
                      textTransform: "none",
                      fontSize: "0.9375rem",
                      fontWeight: 600,
                      gap: 0.75,
                      "&:hover": { backgroundColor: "primary.dark", opacity: 0.9 },
                    }}
                  >
                    <EditOutlined sx={{ width: 20, height: 20, color: "#fff" }} />
                    Edit Event
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => shareModalStore.open()}
                    sx={{
                      height: 44,
                      borderRadius: "22px",
                      border: "1px solid #E0E0E0",
                      backgroundColor: "#fff",
                      color: "#000",
                      textTransform: "none",
                      fontSize: "0.9375rem",
                      fontWeight: 500,
                      px: 2.5,
                      gap: 0.75,
                      "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#BDBDBD" },
                    }}
                  >
                    <Box component="img" src={shareIcon} alt="" sx={{ width: 18, height: 18 }} />
                    Share
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      window.open(
                        getGoogleDirectionsUrl(
                          accountStore.userLocation,
                          event.location!.coordinate!,
                        ),
                        "_blank",
                      )
                    }
                    sx={{
                      height: 44,
                      borderRadius: "22px",
                      border: "1px solid #E0E0E0",
                      backgroundColor: "#fff",
                      color: "#000",
                      textTransform: "none",
                      fontSize: "0.9375rem",
                      fontWeight: 500,
                      px: 2.5,
                      gap: 0.75,
                      "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#BDBDBD" },
                    }}
                  >
                    <DirectionsOutlined sx={{ width: 18, height: 18, color: event.pinHue || "#FF0000" }} />
                    Directions
                  </Button>
                </>
              ) : accountStore.isOrganizationProfile ? (
                /* ORG NOT OWNER: Share + Directions; More menu → Report only */
                <>
                  <Button
                    variant="outlined"
                    onClick={() => shareModalStore.open()}
                    sx={{
                      height: 44,
                      borderRadius: "22px",
                      border: "1px solid #E0E0E0",
                      backgroundColor: "#fff",
                      color: "#000",
                      textTransform: "none",
                      fontSize: "0.9375rem",
                      fontWeight: 500,
                      px: 2.5,
                      gap: 0.75,
                      "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#BDBDBD" },
                    }}
                  >
                    <Box component="img" src={shareIcon} alt="" sx={{ width: 18, height: 18 }} />
                    Share
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      window.open(
                        getGoogleDirectionsUrl(
                          accountStore.userLocation,
                          event.location!.coordinate!,
                        ),
                        "_blank",
                      )
                    }
                    sx={{
                      height: 44,
                      borderRadius: "22px",
                      border: "1px solid #E0E0E0",
                      backgroundColor: "#fff",
                      color: "#000",
                      textTransform: "none",
                      fontSize: "0.9375rem",
                      fontWeight: 500,
                      px: 2.5,
                      gap: 0.75,
                      "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#BDBDBD" },
                    }}
                  >
                    <DirectionsOutlined sx={{ width: 18, height: 18, color: event.pinHue || "#FF0000" }} />
                    Directions
                  </Button>
                  <IconButton
                    onClick={handleMoreClick}
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      border: "1px solid #E0E0E0",
                      backgroundColor: "#fff",
                    }}
                  >
                    <MoreHoriz />
                  </IconButton>
                  <Menu
                    anchorEl={moreAnchorEl}
                    open={isMoreMenuOpen}
                    onClose={handleMoreClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{
                      sx: { boxShadow: "0 4px 20px rgba(0,0,0,0.1)", borderRadius: "12px", minWidth: 180, mt: 1 },
                    }}
                  >
                    <MenuItem
                      onClick={() => { handleMoreClose(); reportModalStore.open(); }}
                      sx={{ color: "#AE0606" }}
                    >
                      <ListItemIcon>
                        <Box
                          component="img"
                          src={reportIcon}
                          sx={{
                            width: 20,
                            height: 20,
                            filter: "invert(13%) sepia(85%) saturate(5183%) hue-rotate(355deg) brightness(88%) contrast(106%)",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Report" />
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                /* USER / LOGGED-OUT NOT OWNER: Attend + Follow + Share + Directions; More → Hide + Report */
                <>
                  {(() => {
                    const isAttended = event.isAttended ?? false;
                    const attendedBg = "primary.main";
                    const attendedHover = "primary.dark";
                    const commonSx = {
                      height: 44,
                      textTransform: "none",
                      fontSize: "0.9375rem",
                      fontWeight: 600,
                    } as const;

                    if (isAttended) {
                      return (
                        <Box ref={attendAreaRef} sx={{ flex: 1, height: 44, display: "flex" }}>
                          <Button
                            variant="contained"
                            sx={{
                              ...commonSx,
                              flex: 1,
                              borderRadius: "22px 0 0 22px",
                              backgroundColor: attendedBg,
                              gap: 0.75,
                              boxShadow: "none",
                              "&:hover": { backgroundColor: attendedHover, boxShadow: "none", opacity: 0.9 },
                            }}
                            onClick={() => showSnackbar("You're already attending this event.", "info")}
                          >
                            <CheckCircle sx={{ fontSize: 20 }} />
                            Attending
                          </Button>
                          <Box sx={{ width: "2px", backgroundColor: "#fff", flexShrink: 0 }} />
                          <Button
                            variant="contained"
                            sx={{
                              ...commonSx,
                              borderRadius: "0 22px 22px 0",
                              backgroundColor: attendedBg,
                              px: 1.5,
                              minWidth: 44,
                              boxShadow: "none",
                              "&:hover": { backgroundColor: attendedHover, boxShadow: "none", opacity: 0.9 },
                            }}
                            onClick={() => setIsCalendarPickerOpen(true)}
                          >
                            <CalendarMonth sx={{ fontSize: 20 }} />
                          </Button>
                        </Box>
                      );
                    }

                    return (
                      <Button
                        ref={attendAreaRef as unknown as React.RefObject<HTMLButtonElement>}
                        variant="contained"
                        onClick={() => {
                          if (!accountStore.account) {
                            accountStore.authModalStore.openAuthModal("login");
                            return;
                          }
                          attendEvent();
                        }}
                        sx={{
                          ...commonSx,
                          flex: 1,
                          borderRadius: "22px",
                          backgroundColor: "primary.main",
                          gap: 0.75,
                          "&:hover": { backgroundColor: "primary.dark", opacity: 0.9 },
                        }}
                      >
                        <Box
                          component="img"
                          src={attendIcon}
                          alt=""
                          sx={{ width: 20, height: 20, filter: "brightness(0) invert(1)" }}
                        />
                        Attend
                      </Button>
                    );
                  })()}

                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (accountStore.account) {
                        setState({
                          ...state,
                          confirmTitle: currentFollowState ? "Unfollow Event?" : "Follow Event?",
                          confirmMessage: currentFollowState
                            ? "Would you like to unfollow this event?"
                            : "Would you like to follow this event?",
                          onConfirm: async () => {
                            confirmModalStore.close();
                            followEvent();
                          },
                        });
                        confirmModalStore.open();
                      } else {
                        accountStore.authModalStore.openAuthModal("login");
                      }
                    }}
                    sx={{
                      height: 44,
                      borderRadius: "22px",
                      border: "1px solid #E0E0E0",
                      backgroundColor: "#fff",
                      color: "#000",
                      textTransform: "none",
                      fontSize: "0.9375rem",
                      fontWeight: 500,
                      px: 2.5,
                      gap: 0.75,
                      "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#BDBDBD" },
                    }}
                  >
                    <Box
                      component="img"
                      src={currentFollowState ? followingButton : followButton}
                      alt=""
                      sx={{ width: 18, height: 18 }}
                    />
                    {currentFollowState ? "Following" : "Follow"}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => shareModalStore.open()}
                    sx={{
                      height: 44,
                      borderRadius: "22px",
                      border: "1px solid #E0E0E0",
                      backgroundColor: "#fff",
                      color: "#000",
                      textTransform: "none",
                      fontSize: "0.9375rem",
                      fontWeight: 500,
                      px: 2.5,
                      gap: 0.75,
                      "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#BDBDBD" },
                    }}
                  >
                    <Box component="img" src={shareIcon} alt="" sx={{ width: 18, height: 18 }} />
                    Share
                  </Button>

                  <IconButton
                    onClick={handleMoreClick}
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      border: "1px solid #E0E0E0",
                      backgroundColor: "#fff",
                    }}
                  >
                    <MoreHoriz />
                  </IconButton>

                  <Menu
                    anchorEl={moreAnchorEl}
                    open={isMoreMenuOpen}
                    onClose={handleMoreClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{
                      sx: { boxShadow: "0 4px 20px rgba(0,0,0,0.1)", borderRadius: "12px", minWidth: 180, mt: 1 },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleMoreClose();
                        window.open(
                          getGoogleDirectionsUrl(
                            accountStore.userLocation,
                            event.location!.coordinate!,
                          ),
                          "_blank",
                        );
                      }}
                    >
                      <ListItemIcon>
                        <DirectionsOutlined
                          sx={{ width: 20, height: 20, color: event.pinHue || "#FF0000" }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Directions" />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleMoreClose();
                        setState({
                          ...state,
                          confirmTitle: currentHideState ? "Unhide Event?" : "Hide Event?",
                          confirmMessage: currentHideState
                            ? "Would you like to unhide this event?"
                            : "Would you like to hide this event?",
                          onConfirm: async () => {
                            confirmModalStore.close();
                            hideEvent();
                          },
                        });
                        confirmModalStore.open();
                      }}
                    >
                      <ListItemIcon>
                        <Box
                          component="img"
                          src={currentHideState ? unhideButton : hideButton}
                          sx={{ width: 20, height: 20 }}
                        />
                      </ListItemIcon>
                      <ListItemText primary={currentHideState ? "Unhide" : "Hide"} />
                    </MenuItem>
                    <MenuItem
                      onClick={() => { handleMoreClose(); reportModalStore.open(); }}
                      sx={{ color: "#AE0606" }}
                    >
                      <ListItemIcon>
                        <Box
                          component="img"
                          src={reportIcon}
                          sx={{
                            width: 20,
                            height: 20,
                            filter: "invert(13%) sepia(85%) saturate(5183%) hue-rotate(355deg) brightness(88%) contrast(106%)",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Report" />
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Stack>

          {/* Categories Section */}
          {event.categories && event.categories.length > 0 && (
            <>
              <Divider sx={{ mx: -3, my: 2.5, borderColor: "#E0E0E0" }} />
              <Box sx={{ mb: 2 }}>
                <SectionHeader>Categories</SectionHeader>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {event.categories.map((category, index) => (
                    <Box
                      key={category.id || index}
                      sx={{
                        bgcolor: "transparent",
                        color: "#000",
                        fontWeight: 500,
                        fontSize: "0.8125rem",
                        height: "32px",
                        borderRadius: "16px",
                        padding: "0 14px",
                        display: "inline-flex",
                        alignItems: "center",
                        border: "1px solid #E0E0E0",
                      }}
                    >
                      {category.name}
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}

          {/* Hosted by Section */}
          {event.organization && (
            <>
              <Divider sx={{ mx: -3, my: 2.5, borderColor: "#E0E0E0" }} />
              <Box sx={{ mb: 2 }}>
                <SectionHeader>Hosted by</SectionHeader>
                <Box
                  onClick={() => onOrganizationClick(event.organization!)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      overflow: "hidden",
                      flexShrink: 0,
                      backgroundColor: "#f0f0f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {event.organization.imageUrl ? (
                      <img
                        src={event.organization.imageUrl}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <ImageIcon
                        sx={{ fontSize: "1.5rem", color: "#bdbdbd" }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                      {event.organization.name}
                    </Typography>
                    {!isCheckingSubscription && hasActiveSubscription && (
                      <CheckCircle
                        sx={{ fontSize: "1rem", color: "#4CAF50" }}
                      />
                    )}
                  </Box>
                  <ChevronRight sx={{ color: "#757575" }} />
                </Box>
                {event.organization.isDigifreeSubscribed && (
                  <Box sx={{ mt: 2 }}>
                    <DigiFreeBadge
                      onClick={() => setIsDigiFreeModalOpen(true)}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Venue Section */}
          {event.venueId && (
            <>
              <Divider sx={{ mx: -3, my: 2.5, borderColor: "#E0E0E0" }} />
              <Box sx={{ mb: 2 }}>
                <SectionHeader>Venue</SectionHeader>
                <Box
                  onClick={() => {
                    const venueOrg = (event as any).venue?.organization;
                    if (venueOrg) {
                      onOrganizationClick(venueOrg, false, true);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "8px",
                      overflow: "hidden",
                      flexShrink: 0,
                      backgroundColor: "#f0f0f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {(event as any).venueOrganizationImageUrl ? (
                      <Box
                        component="img"
                        src={(event as any).venueOrganizationImageUrl}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <ImageIcon
                        sx={{ fontSize: "1.5rem", color: "#bdbdbd" }}
                      />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                      {(event as any).venue?.name}
                    </Typography>
                    {formatAddressLines((event as any).venue?.address)
                      .slice(0, 2)
                      .map((line, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{ color: "#757575", lineHeight: 1.4 }}
                        >
                          {line}
                        </Typography>
                      ))}
                  </Box>
                  <ChevronRight sx={{ color: "#757575" }} />
                </Box>
                {(event as any).venue?.organization?.isDigifreeSubscribed && (
                  <Box sx={{ mt: 2 }}>
                    <DigiFreeBadge
                      onClick={() => setIsDigiFreeModalOpen(true)}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Vendors Section */}
          {event.vendors && event.vendors.length > 0 && (
            <>
              <Divider sx={{ mx: -3, my: 2.5, borderColor: "#E0E0E0" }} />
              <Box sx={{ mb: 2 }}>
                <SectionHeader>Vendors</SectionHeader>
                <Box
                  onClick={() => setIsVendorsModalOpen(true)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    mt: 2,
                  }}
                >
                  <Typography fontWeight={500}>View Vendors List</Typography>
                  <ChevronRight sx={{ color: "#757575" }} />
                </Box>

                {/* <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {event.vendors.map((vendor, index) => (
                    <Box
                      key={vendor.id || index}
                      sx={{
                        bgcolor: "transparent",
                        color: "#000",
                        fontWeight: 500,
                        fontSize: "0.8125rem",
                        height: "32px",
                        borderRadius: "16px",
                        padding: "0 14px",
                        display: "inline-flex",
                        alignItems: "center",
                        border: "1px solid #E0E0E0",
                      }}
                    >
                      {vendor.name}
                    </Box>
                  ))}
                </Box> */}
              </Box>
            </>
          )}

          {/* Event Details (Description) Section */}
          {event.description && (
            <>
              <Divider sx={{ mx: -3, my: 2.5, borderColor: "#E0E0E0" }} />
              <Box sx={{ mb: 2 }}>
                <SectionHeader>Event Details</SectionHeader>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#424242",
                    fontSize: "0.9375rem",
                    lineHeight: 1.6,
                    whiteSpace: "pre-line",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {event.description}
                </Typography>
              </Box>
            </>
          )}

          {/* Location & Map Section */}
          <Divider sx={{ mx: -3, my: 2.5, borderColor: "#E0E0E0" }} />
          <Box sx={{ mb: 3 }}>
            <SectionHeader>Location</SectionHeader>
            {event.location?.coordinate && (
              <Box
                sx={{
                  width: "100%",
                  height: 200,
                  borderRadius: "12px",
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <EventLocationMap
                  latitude={Number(event.location.coordinate.latitude || 0)}
                  longitude={Number(event.location.coordinate.longitude || 0)}
                  pinHue={event.pinHue}
                  iconUrl={(event as any).iconUrl || event.IconUrl}
                  height={200}
                />
              </Box>
            )}
            {/* Redundant address and directions moved to venue row */}

            {event.parkingInfo && (
              <Box sx={{ mt: 2 }}>
                <SectionHeader>Parking Information</SectionHeader>
                <Typography variant="body2" sx={{ color: "#424242" }}>
                  {event.parkingInfo}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <CalendarPickerDialog
        anchorRef={attendAreaRef}
        open={isCalendarPickerOpen}
        onClose={() => setIsCalendarPickerOpen(false)}
        event={calendarEventData}
        onIcsDownload={downloadCalendarFile}
      />

      {/* Modals (unchanged) */}
      <ConfirmModal
        store={confirmModalStore}
        title={state?.confirmTitle ?? ""}
        message={state?.confirmMessage ?? ""}
        onConfirm={state.onConfirm}
        onCancel={() => confirmModalStore.close()}
      />
      <ShareModal
        store={shareModalStore}
        title="Share Event"
        url={getEventShareUrl(event.id)}
      />
      <ReportEventModal
        store={reportModalStore}
        title="Report Event"
        onSubmit={(reason) => {
          reportEvent(reason);
          reportModalStore.close();
        }}
      />
      <RecurringEventEditModal
        open={editRecurringModalStore.isOpen && isRecurringSeries}
        onClose={() => editRecurringModalStore.close()}
        onEdit={handleEditWithScope}
        eventName={event.name}
        hasParent={!!event.parentEventId}
      />
      <DigiFreeModal
        open={isDigiFreeModalOpen}
        onClose={() => setIsDigiFreeModalOpen(false)}
      />
      <SwitchToUserAccountModal
        open={isOrgBookingDialogOpen}
        onClose={() => setIsOrgBookingDialogOpen(false)}
        onSwitched={() =>
          showSuccessMessage(
            "Switched to user account. You can now book tickets.",
          )
        }
      />
      {isSmallScreen ? (
        isVendorsModalOpen && (
          <Box
            sx={{
              position: "fixed",
              top: { xs: "60px", sm: "64px" },
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1400,
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #E0E0E0" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <IconButton
                  onClick={() => setIsVendorsModalOpen(false)}
                  aria-label="Back to event details"
                  sx={{ color: "#000" }}
                >
                  <ArrowBack sx={{ fontSize: "1.25rem" }} />
                </IconButton>
                <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>
                  Vendors
                </Typography>
                <Box sx={{ width: 40, height: 40 }} />
              </Box>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto", px: 3, pb: 2 }}>
              <List sx={{ py: 0 }}>
                {(event.vendors || []).map((vendor, index) => (
                  <ListItemButton
                    key={(vendor as any).id || index}
                    onClick={() =>
                      openVendorOrganization((vendor as any).organizationId)
                    }
                    sx={{ px: 0, py: 1.25, borderBottom: "1px solid #F1F1F1" }}
                  >
                    <ListItemAvatar>
                      <Avatar src={(vendor as any).organizationImageUrl} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={vendor.name}
                      primaryTypographyProps={{
                        fontSize: "0.95rem",
                        fontWeight: 500,
                      }}
                    />
                    <ChevronRight sx={{ color: "#757575" }} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Box>
        )
      ) : (
        <Dialog
          open={isVendorsModalOpen}
          onClose={() => setIsVendorsModalOpen(false)}
          fullWidth={true}
          PaperProps={{
            sx: {
              borderRadius: "1rem",
              boxShadow: "rgba(0, 0, 0, 0.16) 0px 4px 16px",
              maxWidth: APP_MODAL_MAX_WIDTH,
              width: "100%",
              height: "90vh",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <DialogTitle sx={{ px: 1.5, py: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <IconButton
                onClick={() => setIsVendorsModalOpen(false)}
                aria-label="Back to event details"
                sx={{ color: "#000" }}
              >
                <ArrowBack sx={{ fontSize: "1.25rem" }} />
              </IconButton>
              <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>
                Vendors
              </Typography>
              <Box sx={{ width: 40, height: 40 }} />
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 0, flex: 1, overflowY: "auto" }}>
            <List sx={{ py: 0 }}>
              {(event.vendors || []).map((vendor, index) => (
                <ListItemButton
                  key={(vendor as any).id || index}
                  onClick={() =>
                    openVendorOrganization((vendor as any).organizationId)
                  }
                  sx={{ px: 0, py: 1.25, borderBottom: "1px solid #F1F1F1" }}
                >
                  <ListItemAvatar>
                    <Avatar src={(vendor as any).organizationImageUrl} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={vendor.name}
                    primaryTypographyProps={{
                      fontSize: "0.95rem",
                      fontWeight: 500,
                    }}
                  />
                  <ChevronRight sx={{ color: "#757575" }} />
                </ListItemButton>
              ))}
            </List>
          </DialogContent>
        </Dialog>
      )}
      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
    </CardContainer>
  );
};

/* ---------- tiny helper components and styles ---------- */

const CardContainer = styled("div")({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%", // Fill parent (ViewEventModal DialogContent)
  maxHeight: "90vh",
  overflow: "hidden", // inner area will scroll
  background: "#fff",
});

const BannerWrapper = styled(Box)({
  position: "relative",
  top: 0,
  zIndex: 4,
  width: "100%",
  aspectRatio: "16 / 9",
  height: "auto",
  overflow: "hidden",
  display: "block",
});

const InfoRow = styled(Box)({
  display: "flex",
  gap: 14,
  alignItems: "flex-start",
  color: "inherit",
});

const SectionHeader = ({ children }: PropsWithChildren) => (
  <Typography
    variant="body2"
    sx={{ fontWeight: 700, fontSize: "1rem", mb: 1.25, color: "#000" }}
  >
    {children}
  </Typography>
);

/* Pricings component */
const Pricings = observer(
  ({
    pricings,
    selectedPricingId,
    onSelectPricing,
    selectable,
  }: {
    pricings?: Pricing[];
    selectedPricingId?: string;
    onSelectPricing?: (pricingId: string) => void;
    selectable?: boolean;
  }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        width: "100%",
      }}
    >
      {pricings?.map((pricing, i) => {
        const isSelectable =
          selectable && !!pricing.id && (pricing.price ?? 0) > 0;
        const isSelected = isSelectable && pricing.id === selectedPricingId;

        return (
          <Box
            key={pricing.id || `eventPricing${i}`}
            onClick={() => {
              if (isSelectable && pricing.id) {
                onSelectPricing?.(pricing.id);
              }
            }}
            sx={{
              border: isSelectable ? "1px solid" : "1px solid transparent",
              borderColor: isSelected ? "primary.main" : "#E0E0E0",
              borderRadius: "12px",
              px: 1.25,
              py: 0.9,
              cursor: isSelectable ? "pointer" : "default",
              backgroundColor: isSelected ? "rgba(25, 118, 210, 0.08)" : "#fff",
              transition: "all 0.2s ease",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#000", fontSize: "0.875rem", fontWeight: 600 }}
            >
              {pricing.description || "Ticket option"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#424242", fontSize: "0.875rem", fontWeight: 400 }}
            >
              ${pricing.price?.toFixed(2)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  ),
);
